import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const result = await apiService.login(email, password);
      if (result.ok) {
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
        return { success: true };
      }
      return { success: false, error: result.error || 'Login failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const nameParts = name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const result = await apiService.register(email, password, firstName, lastName);
      if (result.ok) {
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
        return { success: true };
      }
      return { success: false, error: result.error || 'Registration failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
      setUser(null);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateProfile = async (updates) => {
    try {
      const result = await apiService.updateProfile(updates);
      if (result.ok) {
        await checkUser();
        return { success: true };
      }
      return { success: false, error: result.error || 'Update failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signInWithGoogle = async (googleUser) => {
    try {
      const result = await apiService.signInWithGoogle(googleUser);
      if (result.ok) {
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
        return { success: true };
      }
      return { success: false, error: result.error || 'Google sign-in failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkUser,
    updateProfile,
    signInWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
